import { Avatar, Form } from 'antd';
import { ColumnsType } from 'antd/es/table';
import AppointmentDatePicker from 'components/AppointmentDatePicker';
import Box from 'components/Box';
import Button from 'components/Button';
import Modal from 'components/Modal';
import SwitchCalendarType from 'components/SwitchCalendarType';
import Table from 'components/Table';
import { CALENDAR_VIEW_TYPE } from 'features/appointment/services/constants';
import { DATE_FORMAT } from 'features/appointment/utils/format';
import { ReportType } from 'features/report/pages/ReportPage/constants';
import reportApis from 'features/report/services/apis';
import reportSelectors from 'features/report/services/selectors';
import { IApiSendReportEmailsCpaParam } from 'features/report/services/types/api';
import { IPrintCheckHistoryItem, IPrintCheckHistoryParams, IPrintCheckHistoryResData } from 'features/report/services/types/check';
import settingServiceSelectors from 'features/settingService/services/selectors';
import moment from 'moment';
import { useRef, useState } from 'react';
import { IResponseDataBody } from 'services/response';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import toast from 'utils/toast';
import { IPrintCheckInfoDataSubmitValues } from '../PrintCheck/type';
import ReceiptPrinter from '../PrintCheck/ReceiptPrinter';
import { useReactToPrint } from 'react-to-print';
import { delay } from 'lodash';
import dayjs from 'dayjs';
import shopSelectors from 'services/shop/selectors';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import EditPrintCheckModal, { EditPrintCheckModalRef } from '../EditPrintCheckModal';
import ReceiptCPAPrinter from '../PrintCheck/ReceiptCPAPrinter';
import ModalConfirm, { useModalConfirmRef } from 'components/Modal/ModalConfirm';
import usePrintCPAList from 'features/report/services/hooks/usePrintCPAList';

interface IFormValues {
  viewType: CALENDAR_VIEW_TYPE;
  startEndDate: { startTime: string; endTime: string };
  page: number;
  emails: string[];
}
const PAGE_SIZE = 10;

const ButtonPrintCheckHistory = () => {
  const viewType = reportSelectors.getViewType()?.[ReportType.EMPLOYEE_SALARY] || CALENDAR_VIEW_TYPE.DAY_VIEW;
  const dataAppointment = reportSelectors.getParams()?.[ReportType.EMPLOYEE_SALARY];
  const [pagination, setPagination] = useState({ current: 1, pageSize: PAGE_SIZE, total: 0 });
  const [dataSource, setDataSource] = useState<IPrintCheckHistoryItem[]>([]);
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const listMailCPA = settingServiceSelectors.getListMailCPA();
  const printCheckSetting = shopSelectors.data.printCheckSetting();
  const printRef = useRef<HTMLDivElement>(null);
  const printCPARef = useRef<HTMLDivElement>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [checkData, setCheckData] = useState<IPrintCheckInfoDataSubmitValues[]>([]);
  const [dataPrintCPA, setDataPrintCPA] = useState<IPrintCheckHistoryItem[]>([]);
  const setLoadingPage = useSetLoadingPage();
  const modalConfirm = useModalConfirmRef();
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const { data: printCPAData, mutate } = usePrintCPAList();

  const editPrintCheckModalRef = useRef<EditPrintCheckModalRef>(null);

  const onManualSelect = (staffId: string) => {
    if (selectedRowKeys.includes(staffId)) {
      setSelectedRowKeys(selectedRowKeys.filter(key => key !== staffId));
    } else {
      setSelectedRowKeys([...selectedRowKeys, staffId]);
    }
  };

  const onRowClick = (record: IPrintCheckHistoryItem) => {
    return {
      onClick: () => onManualSelect(record.id.toString()),
    };
  };


  const fetchData = async (_params: IPrintCheckHistoryParams) => {
    try {
      setLoading(true);
      const res: IResponseDataBody<IPrintCheckHistoryResData> = await reportApis.getPrintCheckHistory(_params);
      if (res.data?.data) {
        setDataSource(res.data.data.content);
        setSelectedRowKeys([]);
        setPagination({
          current: _params.page || 1,
          pageSize: _params.size || PAGE_SIZE,
          total: res.data.data.totalElements,
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (values: any) => {
      const data = values as IFormValues;
      const params: IPrintCheckHistoryParams = {
        page: data.page,
        size: PAGE_SIZE,
        startDate: data.startEndDate.startTime,
        endDate: data.startEndDate.endTime,
      };
    fetchData(params);

    mutate({
      page: 1,
      size: 1000,
      startDate: data.startEndDate.startTime,
      endDate: data.startEndDate.endTime,
    });
  };

  const onPress = () => {
    setVisible(true);
    setSelectedRowKeys([]);
    form.resetFields();
    if (dataAppointment) {
      form.setFieldValue('startEndDate', { startTime: dataAppointment.startTime, endTime: dataAppointment.endTime });
    }
    if (viewType) {
      form.setFieldValue('viewType', viewType);
    }

    form.setFieldValue('emails', listMailCPA);
    form.submit();
  };
  const handleChangePage = (page: number) => {
    form.setFieldValue('page', page);
    form.submit();
  };

  const divRefs = useRef<HTMLDivElement[]>([]);
  const printIndex = useRef(0);
  const oPrintMultiple = useReactToPrint({
    content: () => divRefs.current[printIndex.current] || null,
    onAfterPrint: () => {
      if (printIndex.current < divRefs.current.length - 1) {
        printIndex.current++;
        delay(oPrintMultiple, 100);
      } else {
        setLoadingPage(false);
        setSelectedRowKeys([]);
      }
    },
  });
  const handlePrint = () => {
    const receipts = (printRef.current?.querySelectorAll('.receipt-print') || []) as HTMLDivElement[];
    if (!receipts.length) return setLoadingPage(false);
    printIndex.current = 0;
    divRefs.current = receipts;
    setTimeout(oPrintMultiple, 500);
  };

  const onPressRePrint = () => {
    setLoadingPage(true);
    const dataPrint: IPrintCheckHistoryItem[] = [];
    selectedRowKeys.forEach(key => {
      const record = dataSource.find(item => item.id.toString() === key);
      if (record) {
        dataPrint.push(record);
      }
    });
    const base64 = printCheckSetting?.signature ? 'data:image/png;base64,' + printCheckSetting?.signature || '' : '';
    setCheckData(dataPrint.map(dataDashboard => {
      const result: IPrintCheckInfoDataSubmitValues = ({
        staffId: dataDashboard?.staffId || '',
        staffName: dataDashboard.staffName,
        total: dataDashboard?.amount,
        totalCheck: dataDashboard?.amount,
        date: dataDashboard?.createdDate ? dayjs(dataDashboard?.createdDate, 'MM-DD-YYYY HH:mm A') : dayjs(),
        checkNo: dataDashboard?.checkNo,
        checkBank: {
          bankName: printCheckSetting?.bankName || '',
          bankAddress: printCheckSetting?.bankAddress || '',
          bankWebsite: printCheckSetting?.bankWebsite || '',
          bankLogo: printCheckSetting?.bankLogo || '',
        },
        signature: base64,
        forCheckNote: printCheckSetting?.forCheckNote || '',
        companyLogo: printCheckSetting?.companyLogo || '',
      });
      return result;
    }));
    delay(() => {
      handlePrint();
    }, 500);
  };


  const handlePrintCPA = () => {
    const receipts = (printCPARef.current?.querySelectorAll('.receipt-print-cpa') || []) as HTMLDivElement[];
    if (!receipts.length) return setLoadingPage(false);
    divRefs.current = receipts;
    setTimeout(oPrintMultiple, 500);
  };

  const onPressRePrintCPA = () => {
    setLoadingPage(true);
    if (selectedRowKeys.length === 0) {
      setDataPrintCPA(printCPAData);
    }
    else {
      const dataPrint: IPrintCheckHistoryItem[] = [];
      selectedRowKeys.forEach(key => {
        const record = dataSource.find(item => item.id.toString() === key);
        if (record) {
          dataPrint.push(record);
        }
      });

      setDataPrintCPA(dataPrint);
    }
    // console.log('dataPrint', dataPrint);
    delay(() => {
      handlePrintCPA();
    }, 500);
  };
  const handleSendCpa = async (record: IPrintCheckHistoryItem) => {
    const values: IFormValues = form.getFieldsValue();
    try {
      setLoading(true);
      const body: IApiSendReportEmailsCpaParam = {
        startDate: values.startEndDate.startTime,
        endDate: values.startEndDate.endTime,
        emails: values.emails,
        staffIds: [record.id.toString()],
      };
      const res = await reportApis.sendReportEmailsCpa(body);
      if (res.data.data) {
        toast.success('Email sent successfully');
      }
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSuccess = () => {
    fetchData({
      page: pagination.current,
      size: pagination.pageSize,
      startDate: form.getFieldValue('startEndDate')?.startTime,
      endDate: form.getFieldValue('startEndDate')?.endTime,
    });
  };

  const handleDelete = async (record: IPrintCheckHistoryItem) => {
    try {
      const res: IResponseDataBody = await reportApis.deletePrintCheckHistory(record.id);
      if (res.data?.data) {
        toast.success('Deleted successfully');
        handleUpdateSuccess();
      }
    } catch (error) {
      toast.error('Failed to delete check history');
    }
  };

  const onDelete = async (record: IPrintCheckHistoryItem) => {
    modalConfirm.current?.open(`Are you sure you want to delete check history for ${record.staffName}?`, async () => handleDelete(record));
  };

  const columns: ColumnsType<IPrintCheckHistoryItem> = [
    {
      title: 'NO.',
      dataIndex: 'checkNo',
      key: 'checkNo',
      align: 'left',
      render: (text: string) => text?.trim() ? text : '--',
    },
    {
      title: 'STAFF NAME',
      dataIndex: 'staffName',
      key: 'staffName',
      align: 'left',
      render: (_, record: IPrintCheckHistoryItem) => {
        return (
          <Box display='flex' alignItems='center' gap='1'>
            <Avatar src={record.avatar} >{record.staffName?.[0] || 'A'}</Avatar>
            <span>{record.staffName || '--'}</span>
          </Box>
        );
      }
    },
    {
      title: 'DATE & TIME',
      dataIndex: 'createdDate',
      key: 'createdDate',
      align: 'left',
      render: (text: string) => text?.trim() ? text : '--',
    },
    {
      title: 'AMOUNT',
      dataIndex: 'amount',
      key: 'amount',
      align: 'left',
      render: (text: number) => text ? formatCurrency(text) : '--',
    },
    {
      title: 'FOR',
      dataIndex: 'forNote',
      key: 'forNote',
      align: 'left',
      render: (text: string) => text?.trim() ? text : '--',
    },
    {
      title: 'ACTION',
      key: 'actions',
      align: 'center',
      width: 200,
      render: (_, record: IPrintCheckHistoryItem) => {
        return (
          <Box display='flex' justifyContent='center' gap='2'>
              {/* <ActionButton
                onClick={() => handleSendCpa(record)}
              >
                Send CPA
              </ActionButton> */}
              <Button
                ntype='WHITE_BLUE'
                height={2.5}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (editPrintCheckModalRef.current) {
                      editPrintCheckModalRef.current.open(record.amount, record.id);
                    }
                  }
                }
              >
                Edit
              </Button>
              <Button
                ntype='DANGER'
                height={2.5}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onDelete(record);
                }}
              >
                Delete
              </Button>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Button width='auto' cardCashier ntype='SECONDARY' className='btn-print' icon="print" onClick={onPress}>
        Check History
      </Button>
      <Modal
        modalTitle="Printed Checks History"
        visible={visible}
        onClose={() => setVisible(false)}
        footer={null}
        width={1200}
        destroyOnClose
        noneBodyStyle
        v2
      >
        <Container>
          <FixedTopSection>
            <FilterBox form={form} onFinish={handleFilter} initialValues={{
              viewType: CALENDAR_VIEW_TYPE.DAY_VIEW,
              startEndDate: { startTime: moment().format(DATE_FORMAT), endTime: moment().format(DATE_FORMAT) },
              page: 1,
              emails: listMailCPA,
            }}>
              <Form.Item name={'viewType'} noStyle>
                <SwitchCalendarType submitOnChange onSubmit={(params) => {
                  form.setFieldsValue({ startEndDate: params });
                }} />
              </Form.Item>
              <Form.Item noStyle shouldUpdate dependencies={['viewType']}>{() => {
                const viewType = form.getFieldValue('viewType');
                return (
                  <Form.Item name={'startEndDate'} noStyle>
                    <AppointmentDatePicker viewType={viewType} onChange={() => form.submit()} />
                  </Form.Item>
                );
              }}</Form.Item>
              <Form.Item name={'page'} noStyle />
              <Form.Item name={'emails'} noStyle />
              <Button htmlType='button' disabled={!dataSource.length || !selectedRowKeys.length} width='auto' cardCashier ntype='SECONDARY' className='btn-print' icon="print" onClick={onPressRePrint}>
                Re-Print
              </Button>
              <Button htmlType='button' width='auto' cardCashier ntype='PRIMARY' className='btn-print' icon="printWhite" onClick={onPressRePrintCPA}>
                Re-Print CPA
              </Button>
              <>
                <div style={{ display: 'none' }} ref={printRef}>
                  {checkData.map(data =>
                    <div key={data.staffId} style={{ background: '#FFF' }}>
                      <ReceiptPrinter manual value={data} className='receipt-print' />
                    </div>
                  )}
                </div>
              </>
              <>
                <div style={{ display: 'none', background: '#FFF' }} ref={printCPARef}>
                  <ReceiptCPAPrinter value={dataPrintCPA} className='receipt-print-cpa' />
                </div>
              </>
            </FilterBox>
          </FixedTopSection>
          <ScrollableTableSection>
            <TableStyled>
              <Table
                columns={columns}
                dataSource={dataSource}
                rowKey="id"
                loading={loading}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: false,
                }}
                handleChangePage={handleChangePage}
                rowSelection={rowSelection}
                onRow={onRowClick}
              />
            </TableStyled>
          </ScrollableTableSection>
          <FixedBottomSection>
            <Button ntype='SECONDARY' width='100%' onClick={() => setVisible(false)}>
              Close
            </Button>
          </FixedBottomSection>
        </Container>
      </Modal>
      <EditPrintCheckModal ref={editPrintCheckModalRef} onSuccess={handleUpdateSuccess} />
      <ModalConfirm ref={modalConfirm} />
    </>
  );
};

export default ButtonPrintCheckHistory;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 250px);
  max-height: 900px;
  padding: 16px;
  overflow: hidden;
`;

const FixedTopSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex-shrink: 0;
  background: #fff;
  z-index: 10;
`;

const ScrollableTableSection = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 16px;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const FixedBottomSection = styled.div`
  flex-shrink: 0;
  background: #fff;
  z-index: 10;
  padding-top: 16px;
`;

const FilterBox = styled(Form)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const TableStyled = styled.div`
  flex-shrink: 0;
  .ant-table-wrapper {
    overflow: visible;
    .ant-table {
      .ant-table-container {
        .ant-table-content {
          overflow: visible;
          border: none;
          .ant-table-selection-column {
              text-align: center !important;
              .ant-checkbox-wrapper {
                  zoom: 1.8;
              }
          }
          .ant-table-thead {
            overflow: hidden;
            position: sticky;
            top: 0;
            z-index: 10;
            
            th.ant-table-cell {
              position: sticky;
              top: 0;
              z-index: 99;
              background: #6fabb6 !important;
              border-bottom: 1px solid #ccd4dc;
              color: #fff !important;
              height: 52px;
              padding: 8px;
              font-family: Poppins;
              font-size: 14px;
              font-style: normal;
              font-weight: 600;
              line-height: normal;
              position: relative;
              &::before {
                content: none;
              }
              
              &:not(:last-child)::after {
                content: '';
                position: absolute;
                right: 0;
                top: 8px;
                bottom: 8px;
                width: 2px;
                background: rgba(255, 255, 255, 0.3);
                z-index: 1;
              }
            }
            
            tr th:first-child {
              border-top-left-radius: 8px !important;
            }
            
            tr th:last-child {
              border-top-right-radius: 8px !important;
            }
          }
          .ant-table-tbody {
            .ant-table-row {
              background: #ffffff;
              &:nth-child(even) {
                background: #eef4ff;
              }
            }
          }
        }
      }
    }
    
    .ant-table .ant-table-thead th.ant-table-cell {
      position: sticky;
      top: 0;
      z-index: 99;
      background: #6fabb6 !important;
      border-bottom: 1px solid #ccd4dc;
      color: #fff !important;
      height: 52px;
      padding: 8px;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      position: relative;
      &::before {
        content: none;
      }
      
      &:not(:last-child)::after {
        content: '';
        position: absolute;
        right: 0;
        top: 8px;
        bottom: 8px;
        width: 2px;
        background: rgba(255, 255, 255, 0.3);
      }
    }
    
    .ant-table .ant-table-thead tr th:first-child {
      border-top-left-radius: 8px !important;
    }
    
    .ant-table .ant-table-thead tr th:last-child {
      border-top-right-radius: 8px !important;
    }
    
    .ant-table-tbody td.ant-table-cell {
      height: auto;
      padding: 2px 8px;
      color: #131921 !important;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 22px;
      
      .BODY_1, .CONTENT_1, .CAPTION_1 {
        color: #131921 !important;
      }
    }
    
    .ant-table-tbody .ant-table-row:nth-child(even) td {
      background: #eef4ff;
    }
  }
`;

const PaginationStyled = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 16px;
  flex-shrink: 0;
  background: #fff;
  z-index: 10;

  .ant-pagination {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    
    .ant-pagination-item,
    .ant-pagination-next,
    .ant-pagination-prev {
      border-radius: 3.333px;
      background: #fff;
      border: 1px solid #86909c;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      margin: 0 4px;
      
      a {
        color: #1d2129;
        text-align: center;
        font-family: Poppins;
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
      }

      &.ant-pagination-item-active {
        background: #6FABB6;
        border-color: #6FABB6;
        a {
          color: #fff;
        }
      }

      &:hover:not(.ant-pagination-item-active) {
        background: #E5E6EB;
        border-color: #E5E6EB;
      }
    }

    .ant-pagination-jump-next,
    .ant-pagination-jump-prev {
      .ant-pagination-item-container {
        .ant-pagination-item-link-icon {
          color: #1D2129;
        }
      }
    }

    .ant-pagination-disabled {
      opacity: 0.5;
      cursor: not-allowed;
      
      &:hover {
        background: #fff;
        border-color: #86909c;
      }
    }
  }
`;

const TotalTag = styled.div`
  display: inline-flex;
  padding: 4px 8px;
  border-radius: 5px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  
  &.New {
    background: #FFF1F6;
    color: #D2464F;
  }
  &.Regular {
    background: #F2EEFC;
    color: #845ADF;
  }
  &.VIP {
    background: #FDF4CE;
    color: #EBA33B;
  }
`;

const ActionsBox = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

const ActionButton = styled.button`
  display: flex;
  flex: 1 0 0;
  height: 44px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  border: 1px solid #2d6772;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.4), 0px 4px 8px -3px rgba(45, 35, 66, 0.3);
  
  color: #2d6772;
  text-align: center;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;

  &::after {
    content: '';
    position: absolute;
    inset: -0.5px;
    border-radius: inherit;
    pointer-events: none;
    box-shadow: inset 0px -3px 0px 0px #144b55;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
    
    &::after {
      display: none;
    }
  }

  &.btn-primary {
    background: #0da472;
    border-color: #0da472;
    color: #fff;
    
    &::after {
      box-shadow: inset 0px -3px 0px 0px #0b8d5f;
    }
    
    &:hover:not(:disabled) {
      background: #0b8d5f;
      border-color: #0b8d5f;
    }
    
    &:disabled {
      background: #cccccc;
      border-color: #cccccc;
      color: #666666;
    }
  }
`;
